import { useEffect, useState, useCallback, useRef, type CSSProperties } from 'react'
import { Cast, Images, Pencil, Settings, Tv, WifiOff } from 'lucide-react'
import { VideoPlayer } from './player/VideoPlayer'
import { PlaylistSidebar } from './playlist/PlaylistSidebar'
import { SettingsPanel } from './settings/SettingsPanel'
import { RoomBrand } from './RoomBrand'
import { usePlaybackStore, useSettingsStore, useAuthStore } from '../store'
import { authApi, broadcastApi, roomApi, settingsApi, playlistApi } from '../services/api'
import { BROADCAST_OUTPUT_CHANNEL, dimHexColor, fairShuffle, hexToRgbTriplet } from '../utils'
import type { PlaylistItem, RoomProfile } from '../types'
import { APP_VERSION_LABEL } from '../version'
import { MediaLibrary } from './library/MediaLibrary'


export function App() {
  const [showSettings, setShowSettings] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isOwner, setIsOwner] = useState(false)
  const [canSavePlayback, setCanSavePlayback] = useState(false)
  const [rooms, setRooms] = useState<RoomProfile[]>([])
  const [pathname, setPathname] = useState(window.location.pathname)

  const {
    isSidebarOpen,
    isTheaterMode,
    currentVideoId,
    sortOrder,
    setPlaylist,
    setCurrentVideo,
    setVolume,
    setSortOrder,
    setTheaterMode,
    setBumperRule,
    setActiveSavedPlaylist,
    playlist,
    clearQueue,
  } = usePlaybackStore()

  const { settings, setSettings, isLoaded } = useSettingsStore()
  const { setProviderAuth } = useAuthStore()
  const isBroadcastRoute = /\/broadcast\/?$/.test(pathname)
  const isLibraryRoute = /\/app\/library\/?$/.test(pathname)
  const playlistRequestRef = useRef<AbortController | null>(null)

  // ─── Load playlist ─────────────────────────────────────────────────────────

  const loadPlaylist = useCallback(async (
    sort: typeof sortOrder,
    currentSettings: typeof settings,
  ) => {
    if (!currentSettings?.folderId) return
    playlistRequestRef.current?.abort()
    const controller = new AbortController()
    playlistRequestRef.current = controller

    try {
      const pl = await playlistApi.getPlaylist(sort, controller.signal)
      let items: PlaylistItem[] = pl.items.map((item, i) => ({ ...item, playlistIndex: i }))

      if (sort === 'shuffle') {
        items = fairShuffle(items).map((item, i) => ({ ...item, playlistIndex: i }))
      }

      setPlaylist(items)
      if (currentSettings.lastVideoId) {
        const idx = items.findIndex((i) => i.id === currentSettings.lastVideoId)
        if (idx >= 0) {
          setCurrentVideo(idx)
          return
        }
      }
      if (items.length > 0) setCurrentVideo(0)
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return
      console.error('Playlist load failed:', cause)
      setRefreshError(cause instanceof Error ? cause.message : 'Could not load playlist')
    } finally {
      if (playlistRequestRef.current === controller) {
        playlistRequestRef.current = null
      }
    }
  }, [setPlaylist, setCurrentVideo])

  // ─── Network status ────────────────────────────────────────────────────────

  useEffect(() => {
    const onOnline = () => setIsOnline(true)
    const onOffline = () => setIsOnline(false)
    const onPopState = () => setPathname(window.location.pathname)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  useEffect(() => {
    const refreshCapability = () => { void authApi.getStatus().then(status => {
      setCanSavePlayback(status.viewer.canSavePlayback)
    }).catch(() => setCanSavePlayback(false)) }
    const timer = window.setInterval(refreshCapability, 30000)
    window.addEventListener('focus', refreshCapability)
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refreshCapability) }
  }, [])

  // ─── Initial load ──────────────────────────────────────────────────────────

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [userSettings, authStatus, roomList] = await Promise.all([
          settingsApi.getSettings(),
          authApi.getStatus(),
          roomApi.list(),
        ])

        setSettings(userSettings)
        setProviderAuth('google', authStatus.google)
        setProviderAuth('onedrive', authStatus.onedrive)
        setIsOwner(authStatus.viewer.isOwner)
        setCanSavePlayback(authStatus.viewer.canSavePlayback)
        setRooms(roomList)

        if (userSettings.volume !== undefined) {
          setVolume(userSettings.volume)
        }
        if (userSettings.sortOrder) {
          setSortOrder(userSettings.sortOrder)
        }

        if (!userSettings.folderId && !isBroadcastRoute) {
          setShowSettings(true)
        }
      } catch (e) {
        console.error('Bootstrap failed:', e)
        if (!isBroadcastRoute) setShowSettings(true)
      }
    }

    void bootstrap()
  }, [isBroadcastRoute])

  // ─── Reload playlist on sort change ───────────────────────────────────────

  useEffect(() => {
    if (!isLoaded || !settings?.folderId) return
    void loadPlaylist(sortOrder, settings)
    return () => playlistRequestRef.current?.abort()
  }, [isLoaded, settings?.folderId, sortOrder, loadPlaylist])

  useEffect(() => {
    if (!settings) return
    setBumperRule({
      enabled: settings.bumperEnabled,
      videoId: settings.bumperVideoId,
      frequency: settings.bumperFrequency,
      placement: settings.bumperPlacement,
      skipAllowed: settings.bumperSkipAllowed,
      volume: settings.bumperVolume,
    })
  }, [settings, setBumperRule])

  const refreshPlaylist = useCallback(async (currentSettings: NonNullable<typeof settings>) => {
    setIsRefreshing(true)
    setRefreshError(null)
    try {
      const { jobId } = await playlistApi.scanFolder()
      for (;;) {
        const status = await playlistApi.getScanStatus(jobId)
        if (status.done) {
          if (status.status === 'failed') throw new Error(status.error ?? 'Playlist refresh failed')
          break
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1_000))
      }
      await loadPlaylist(sortOrder, currentSettings)
    } catch (cause) {
      console.error('Playlist refresh failed:', cause)
      setRefreshError(cause instanceof Error ? cause.message : 'Playlist refresh failed')
    } finally {
      setIsRefreshing(false)
    }
  }, [sortOrder, loadPlaylist])

  const handleRefresh = useCallback(() => {
    if (settings) void refreshPlaylist(settings)
  }, [settings, refreshPlaylist])

  const handleFolderSelected = useCallback(() => {
    setShowSettings(false)
    void settingsApi.getSettings()
      .then((s) => {
        setSettings(s)
        void refreshPlaylist(s)
      })
      .catch((cause) => {
        console.error('Folder selection refresh failed:', cause)
        setRefreshError(cause instanceof Error ? cause.message : 'Could not load selected folder')
      })
  }, [refreshPlaylist, setSettings])

  const handleSelectRoom = useCallback(async (roomId: string) => {
    try {
      await roomApi.select(roomId)
      clearQueue()
      setActiveSavedPlaylist(null)
      const [nextSettings, roomList] = await Promise.all([settingsApi.getSettings(), roomApi.list()])
      setSettings(nextSettings)
      setRooms(roomList)
      await loadPlaylist(nextSettings.sortOrder, nextSettings)
    } catch (cause) {
      setRefreshError(cause instanceof Error ? cause.message : 'Could not switch rooms')
    }
  }, [clearQueue, loadPlaylist, setActiveSavedPlaylist, setSettings])

  const handleCreateRoom = useCallback(async () => {
    const name = window.prompt('Name the new room')?.trim()
    if (!name) return
    try {
      const room = await roomApi.create(name)
      const roomList = await roomApi.list()
      setRooms(roomList)
      await handleSelectRoom(room.id)
    } catch (cause) {
      setRefreshError(cause instanceof Error ? cause.message : 'Could not create room')
    }
  }, [handleSelectRoom])

  const handleRenameRoom = useCallback(async () => {
    const activeRoom = rooms.find((room) => room.id === settings?.roomId)
    if (!activeRoom) return
    const name = window.prompt('Rename room', activeRoom.name)?.trim()
    if (!name || name === activeRoom.name) return
    try {
      const updated = await roomApi.update(activeRoom.id, { name })
      setRooms((current) => current.map((room) => room.id === updated.id ? updated : room))
    } catch (cause) {
      setRefreshError(cause instanceof Error ? cause.message : 'Could not rename room')
    }
  }, [rooms, settings?.roomId])

  const handleOpenBroadcastOutput = useCallback(() => {
    void broadcastApi.getConfig()
      .then((config) => {
        const target = config.enabled ? config.url : `${window.location.origin}/broadcast`
        window.open(target, 'cloudplayer-broadcast', 'width=1280,height=720')
        if (!currentVideoId || typeof BroadcastChannel === 'undefined') return

        window.setTimeout(() => {
          const channel = new BroadcastChannel(BROADCAST_OUTPUT_CHANNEL)
          channel.postMessage({ type: 'play-video', videoId: currentVideoId })
          channel.close()
        }, 800)
      })
      .catch(() => {
        window.open(`${window.location.origin}/broadcast`, 'cloudplayer-broadcast', 'width=1280,height=720')
      })
  }, [currentVideoId])

  const navigateInApp = useCallback((path: string) => {
    window.history.pushState({}, '', path)
    setPathname(path)
  }, [])

  const handlePlayFromLibrary = useCallback((item: PlaylistItem) => {
    const index = playlist.findIndex((candidate) => candidate.id === item.id && candidate.provider === item.provider)
    if (index >= 0) {
      setCurrentVideo(index)
      navigateInApp('/app')
    }
  }, [navigateInApp, playlist, setCurrentVideo])

  const totalSize = playlist.reduce((acc, v) => acc + v.size, 0)
  const accentColor = settings?.accentColor ?? '#3B8BEB'
  const appName = settings?.appName?.trim() || 'CloudPlayer'

  return (
    <div
      className="flex flex-col h-screen bg-void overflow-hidden"
      style={{
        '--cp-accent-rgb': hexToRgbTriplet(accentColor),
        '--cp-accent-dim-rgb': dimHexColor(accentColor),
      } as CSSProperties}
    >

      {!isTheaterMode && !isBroadcastRoute && (
        <header className="room-header">
          <div className="flex min-w-0 items-center gap-4">
            <RoomBrand name={appName} versionLabel={APP_VERSION_LABEL} />

            {rooms.length > 0 && (
              <div className="flex items-center gap-1 rounded-md border border-border bg-surface px-1">
                <select
                  value={settings?.roomId ?? ''}
                  onChange={(event) => void handleSelectRoom(event.target.value)}
                  disabled={!isOwner}
                  className="max-w-32 bg-transparent px-1.5 py-1 text-xs text-soft outline-none disabled:cursor-default"
                  aria-label="Active room"
                >
                  {rooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}
                </select>
                {isOwner && (
                  <>
                    <button onClick={() => void handleRenameRoom()} className="btn-ghost p-1" title="Rename room" aria-label="Rename room">
                      <Pencil size={11} />
                    </button>
                    <button onClick={() => void handleCreateRoom()} className="btn-ghost p-1" title="Create room" aria-label="Create room">+</button>
                  </>
                )}
              </div>
            )}

            {settings?.folderName && (
              <div className="room-header__library">
                <span className={settings.provider === 'google' ? 'text-warning' : 'text-accent'}>
                  {settings.provider === 'google' ? '◐' : '◑'}
                </span>
                <span className="truncate">{settings.folderName}</span>
              </div>
            )}
          </div>

          <div className="room-header__actions">
            {!isOnline && (
              <div className="flex items-center gap-1 text-xs text-danger">
                <WifiOff size={12} />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}

            <button
              onClick={() => navigateInApp(isLibraryRoute ? '/app' : '/app/library')}
              className="btn-ghost rounded-lg px-2.5 py-2 text-xs font-medium"
              title={isLibraryRoute ? 'Back to player' : 'Open media library'}
            >
              <Images size={15} />
              <span className="hidden sm:inline">{isLibraryRoute ? 'Player' : 'Library'}</span>
            </button>

            <button
              onClick={handleOpenBroadcastOutput}
              className="btn-ghost rounded-lg px-2.5 py-2 text-xs font-medium"
              title="Open broadcast output"
            >
              <Cast size={15} />
              <span className="hidden sm:inline">Broadcast</span>
            </button>

            <button
              onClick={() => setTheaterMode(true)}
              className="btn-ghost p-2 rounded-lg"
              title="Theater mode (T)"
            >
              <Tv size={15} />
            </button>

            {isOwner && (
              <button
                onClick={() => setShowSettings(true)}
                className="btn-ghost p-2 rounded-lg"
                title="Settings"
              >
                <Settings size={15} />
              </button>
            )}
          </div>
        </header>
      )}

      {/* Main content */}
      <div className="relative flex flex-1 overflow-hidden">

        {/* Video area */}
        <div className="flex-1 overflow-hidden">
          <VideoPlayer
            provider={settings?.provider ?? null}
            appName={appName}
            welcomeMessage={settings?.welcomeMessage ?? null}
            isOwner={isOwner}
            canSavePlayback={canSavePlayback}
            isBroadcastMode={isBroadcastRoute}
          />
        </div>

        {/* Sidebar */}
        {isSidebarOpen && !isTheaterMode && !isBroadcastRoute && (
          <div className="w-80 flex-shrink-0 overflow-hidden">
            <PlaylistSidebar
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
              refreshError={refreshError}
              totalSize={totalSize}
              isOwner={isOwner}
            />
          </div>
        )}

        {isLibraryRoute && (
          <MediaLibrary
            folderName={settings?.folderName ?? null}
            isOwner={isOwner}
            isRefreshing={isRefreshing}
            refreshError={refreshError}
            onBack={() => navigateInApp('/app')}
            onRefresh={handleRefresh}
            onPlay={handlePlayFromLibrary}
          />
        )}
      </div>

      {/* Settings modal */}
      {showSettings && !isBroadcastRoute && (
        <SettingsPanel
          onClose={() => setShowSettings(false)}
          onFolderSelected={handleFolderSelected}
        />
      )}
    </div>
  )
}
